/** TEAMS.md § 2.3 — reading rights differ per tier; joining is always by invitation. */
export type TeamPrivacy = 'open' | 'closed' | 'secret';

/**
 * TEAMS.md § 3.1 — there is no team `owner`; a tenant admin can always
 * administer a team.
 *
 * No word is shared with the tenant axis (owner | admin | member | observer).
 * `guest` is reserved for a future tenant role: an external person on a BILLED
 * seat with restricted scope, which is what the industry means by it.
 */
export type TeamRole = 'manager' | 'contributor' | 'viewer';

export interface Team {
  id: string;
  workspaceId: string;
  /** Null for the root team and for every secret team — enforced by CHECK constraint (§ 2.3). */
  parentId: string | null;
  name: string;
  slug: string;
  /** Plain text because it renders directly in compact tree and picker surfaces. */
  description: string | null;
  privacy: TeamPrivacy;
  isRoot: boolean;
  archived: boolean;
  /** Board this team opens; may be a shared scorecard, and may be null. */
  primaryScorecardId: string | null;
}

/** TEAMS.md § 3.5 — who can see this team, as opposed to what changed on it. */
export type TeamAccessEventType =
  | 'member_added'
  | 'member_removed'
  | 'member_role_changed'
  | 'privacy_changed'
  | 'parent_changed'
  | 'break_glass_granted'
  | 'break_glass_relinquished';

export interface TeamAccessEvent {
  id: string;
  teamId: string;
  /** Null when the change came from a migration, a seed or service_role — nobody did it. */
  actorUserId: string | null;
  eventType: TeamAccessEventType;
  /** The person the event is ABOUT, where that differs from the actor. */
  subjectUserId: string | null;
  detail: Record<string, string | null> | null;
  createdAt: string;
}

/**
 * A live break-glass hold on a team (§ 3.5).
 *
 * The row carries the access, rather than recording access RLS was granting
 * anyway: a tenant admin already reads every non-secret team, so this is the
 * only thing that opens a secret one.
 */
export interface BreakGlassGrant {
  id: string;
  teamId: string;
  userId: string;
  grantedAt: string;
  expiresAt: string;
  relinquishedAt: string | null;
}

export function isGrantLive(g: BreakGlassGrant): boolean {
  return !g.relinquishedAt && new Date(g.expiresAt) > new Date();
}

/**
 * What may be shared (TEAMS.md § 4.1, issue #158). Mirrors
 * `entity_shares_type_check` — a type added in one place and not the other
 * either fails to insert or inserts and grants nothing.
 */
export type ShareableEntityType = 'rock' | 'task' | 'issue' | 'metric' | 'idea' | 'scorecard';

export const SHAREABLE_TYPES: ShareableEntityType[] =
  ['rock', 'task', 'issue', 'metric', 'idea', 'scorecard'];

/**
 * Additional READ for one entity, on one other team. Never write — the home
 * team is the only writer (§ 4.1).
 *
 * The list arrives already filtered per viewer: RLS returns a share only if you
 * can read the team it points at, which is what makes "never a count that
 * exceeds the visible list" (§ 4.4) true without the client remembering it.
 */
export interface EntityShare {
  id: string;
  entityType: ShareableEntityType;
  entityId: string;
  teamId: string;
  createdBy: string | null;
  createdAt: string;
  /** May the receiving team keep these metrics by cloning the board? (#165) */
  clonable?: boolean;
  /** Set when this row was materialised by a clone — revocable as a group. */
  derivedFromScorecardId?: string | null;
}

export interface TeamMember {
  id: string;
  teamId: string;
  userId: string;
  /** The raw grant. Effective access is min(tenant ceiling, grant) — see clipRole. */
  role: TeamRole;
}

const RANK: Record<TeamRole, number> = { manager: 3, contributor: 2, viewer: 1 };

/**
 * Effective role: min(tenant ceiling, team grant). Mirrors
 * `user_team_role_effective` in the database (TEAMS.md § 3.1).
 *
 * The UI must show the clip, not the raw grant. A tenant observer holding team
 * `manager` is read-only everywhere, and displaying "manager" without saying why
 * they cannot write is how this becomes a support ticket. It is a recoverable
 * state, not a corrupt one — restoring the tenant role restores the grant.
 */
export function clipRole(grant: TeamRole, tenantRole: string | null | undefined): TeamRole | null {
  if (!tenantRole) return null;
  // Tenant axis on the left, team axis on the right — deliberately different
  // words now, so this line no longer reads as a tautology.
  if (tenantRole === 'observer') return 'viewer';
  return grant;
}

export function isClipped(grant: TeamRole, tenantRole: string | null | undefined): boolean {
  const effective = clipRole(grant, tenantRole);
  return effective !== null && RANK[effective] < RANK[grant];
}

/** Children of `parentId`, name-sorted. Secret teams are never children (§ 2.3). */
export function childrenOf(teams: Team[], parentId: string | null): Team[] {
  return teams
    .filter((s) => !s.isRoot && s.privacy !== 'secret' && s.parentId === parentId)
    .sort((a, b) => a.name.localeCompare(b.name));
}

/**
 * Secret teams sit outside the tree entirely — `privacy = 'secret'` implies
 * `parent_id IS NULL`, which is what stops read-down tunnelling into them. They
 * render as a flat set, never as nodes.
 */
export function secretTeams(teams: Team[]): Team[] {
  return teams.filter((s) => s.privacy === 'secret').sort((a, b) => a.name.localeCompare(b.name));
}

/** A row on a rendered team hierarchy — see `buildTeamTree`. */
export interface TeamTreeNode {
  team: Team;
  depth: number;
  /** Whether this row can be chosen, as opposed to merely placing the ones below it. */
  selectable: boolean;
  /**
   * Why not, when not. One sentence, specific to this row and this act.
   *
   * A generic "unavailable" is worth less than no tooltip at all, because it
   * costs a hover to learn nothing — so the predicate returns the sentence
   * rather than a boolean, and there is nowhere to put a placeholder.
   */
  reason?: string;
}

export interface TeamTree {
  /** The tree, root-down and depth-first, in the order it renders. */
  nodes: TeamTreeNode[];
  /**
   * Secret teams, flat. NOT tree nodes: `privacy = 'secret'` implies
   * `parent_id IS NULL` by CHECK (§ 2.3), which is what stops read-down
   * tunnelling into them. They are a separate array rather than a flag on a node
   * so that no caller can accidentally nest one and misdescribe the invariant
   * that makes them safe.
   */
  secret: TeamTreeNode[];
}

/**
 * Canonical rendered hierarchy. Retains disabled ancestors needed to place a
 * selectable descendant, prunes branches with none, and keeps secret teams flat.
 * `ineligible` supplies the row-specific reason shown by callers.
 */
export function buildTeamTree(
  teams: Team[],
  ineligible: (team: Team) => string | null,
): TeamTree {
  const node = (team: Team, depth: number): TeamTreeNode => {
    const reason = ineligible(team);
    return reason === null
      ? { team, depth, selectable: true }
      : { team, depth, selectable: false, reason };
  };

  // A disabled parent survives only when it provides context for a selectable descendant.
  const walk = (team: Team, depth: number): TeamTreeNode[] => {
    const self = node(team, depth);
    const below = childrenOf(teams, team.id).flatMap((child) => walk(child, depth + 1));
    if (!self.selectable && below.length === 0) return [];
    return [self, ...below];
  };

  const root = teams.find((s) => s.isRoot);
  return {
    nodes: root ? walk(root, 0) : [],
    secret: secretTeams(teams).map((s) => node(s, 0)).filter((n) => n.selectable),
  };
}

/** A team and its descendants, used to prevent cyclic reparenting. */
export function descendantIds(teams: Team[], id: string): Set<string> {
  const out = new Set<string>([id]);
  let grew = true;
  while (grew) {
    grew = false;
    for (const s of teams) {
      if (s.parentId && out.has(s.parentId) && !out.has(s.id)) {
        out.add(s.id);
        grew = true;
      }
    }
  }
  return out;
}

/**
 * Ancestors nearest-first. Secret teams use `[self, root]` so they inherit the
 * tenant vision without gaining tree ancestry; unknown ids fall back to root.
 */
export function ancestorChain(teams: Team[], teamId: string | null | undefined): string[] {
  const rootId = teams.find((s) => s.isRoot)?.id ?? null;
  const byId = new Map(teams.map((s) => [s.id, s]));
  const start = teamId ? byId.get(teamId) : undefined;

  if (!start) return rootId ? [rootId] : [];
  if (start.privacy === 'secret') {
    return rootId && rootId !== start.id ? [start.id, rootId] : [start.id];
  }

  const chain: string[] = [];
  // Guard corrupt client state even though database constraints reject cycles.
  const seen = new Set<string>();
  let cursor: Team | undefined = start;
  while (cursor && !seen.has(cursor.id)) {
    seen.add(cursor.id);
    chain.push(cursor.id);
    cursor = cursor.parentId ? byId.get(cursor.parentId) : undefined;
  }
  return chain;
}

/**
 * The chain configuration resolves along: `ancestorChain`, minus closed
 * ancestors.
 *
 * A closed team does not export its configuration to its descendants — closed
 * is a claim about who participates, and a meeting type is the most operational
 * thing a team owns, carrying its cadence and its roster.
 *
 * The rule keys off the team, never the viewer, so every viewer resolves the
 * same value. A viewer-dependent rule would let two attendees see different
 * agendas for one meeting, and the service-role dispatchers — which have no RLS
 * — would compute a third and mail that one.
 *
 * The starting team is always kept, whatever its privacy: resolving at a team
 * is not inheriting from it.
 */
export function configChain(teams: Team[], teamId: string | null | undefined): string[] {
  const byId = new Map(teams.map((s) => [s.id, s]));
  const chain = ancestorChain(teams, teamId);
  return chain.filter((id, i) => i === 0 || byId.get(id)?.privacy !== 'closed');
}

export function slugify(name: string, taken: string[]): string {
  const base = name.toLowerCase().trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 40) || 'team';
  if (!taken.includes(base)) return base;
  // (workspace_id, slug) is unique, so collisions must be resolved before insert
  // rather than surfaced as a constraint violation.
  for (let n = 2; ; n++) {
    const candidate = `${base}-${n}`;
    if (!taken.includes(candidate)) return candidate;
  }
}
